import type { LevelLite } from './adapters/types';
import { levels as musicLevels } from '../../data/curricula/music/levels';
import { levels as musicWinter2026Levels } from '../../data/curricula/musicWinter2026/levels';

export type ManagedCurriculum = {
  id: string;
  title: string;
  description: string;
  /** Parent-facing copy and artwork used by the public website. */
  publicBlurb?: string;
  publicBadgeUrl?: string;
  published: boolean;
  kind?: 'curriculum' | 'demo_experience';
  /** Code-managed curricula expose public metadata here while lessons stay read-only. */
  contentSource?: 'firestore' | 'code';
  /** Legacy single-value duration. New demo experiences use the min/max range. */
  durationHours?: number;
  durationMinHours?: number;
  durationMaxHours?: number;
  demoCategory?: 'lunch' | 'booth' | 'assembly' | 'classroom' | 'conference' | 'other';
  referencePdf?: {
    id: string;
    title: string;
    url: string;
    kind: 'document';
  };
  levels: LevelLite[];
};

const loop = (curriculumId: string, classNumber: number) => {
  const prefix = `${curriculumId}-${classNumber}`;
  return [
    { id: `${prefix}-warm-up`, type: 'completion', text: 'Warm-up', title: 'Warm-up', plannedMinutes: 5, teachingNotes: '', missionType: 'warm-up' },
    { id: `${prefix}-intro`, type: 'completion', text: 'Intro', title: 'Intro', plannedMinutes: 5, teachingNotes: '', missionType: 'welcome' },
    { id: `${prefix}-reveal`, type: 'completion', text: 'Reveal & Review', title: 'Reveal & Review', plannedMinutes: 10, teachingNotes: '', missionType: 'reveal' },
    { id: `${prefix}-learn`, type: 'completion', text: 'Learn / Practice', title: 'Learn / Practice', plannedMinutes: 10, teachingNotes: '', missionType: 'activity' },
    { id: `${prefix}-creation`, type: 'completion', text: 'Creation', title: 'Creation', plannedMinutes: 20, teachingNotes: '', missionType: 'activity' },
    { id: `${prefix}-reflection`, type: 'completion', text: 'Reflection', title: 'Reflection', plannedMinutes: 10, teachingNotes: '', missionType: 'reflect' },
  ];
};

const make = (id: string, title: string): ManagedCurriculum => ({
  id,
  title,
  description: '12-class curriculum scaffolded around the Overture lesson loop.',
  published: true,
  kind: 'curriculum',
  levels: Array.from({ length: 12 }, (_, index) => {
    const n = index + 1;
    return {
      id: String(n),
      title: `Class ${n}: To be developed`,
      kind: 'required' as const,
      briefSummary: 'Lesson content coming soon.',
      teacherNotes: '',
      additionalNotes: '',
      iCanStatement: '',
      slideDeckUrl: '',
      videoUrl: '',
      resources: [],
      missions: loop(id, n),
    };
  }),
});

export const makeDemoExperience = (id: string, title: string): ManagedCurriculum => ({
  id,
  title,
  description: 'A single-session, 1–4 hour experience.',
  published: false,
  kind: 'demo_experience',
  durationMinHours: 1,
  durationMaxHours: 4,
  demoCategory: 'lunch',
  levels: [{
    id: '1',
    title: 'Experience: To be developed',
    kind: 'required',
    briefSummary: 'Short experience content coming soon.',
    teacherNotes: '',
    additionalNotes: '',
    iCanStatement: '',
    slideDeckUrl: '',
    videoUrl: '',
    resources: [],
    missions: [],
  }],
});

export const scaffoldedDemoExperiences: ManagedCurriculum[] = [
  makeDemoExperience('demo-experience-starter', 'Demo Experience Starter'),
];

export const scaffoldedCurricula: ManagedCurriculum[] = [
  make('entrepreneurship-2026', 'Entrepreneurship 2026'),
  make('movie-making-2026', 'Movie Making 2026'),
  make('game-design-2026', 'Game Design 2026'),
  make('intro-ai-kids-2026', 'Intro to AI for Kids 2026'),
  make('intro-ai-adults-2026', 'Intro to AI for Adults 2026'),
];

export const codeManagedCurricula: ManagedCurriculum[] = [
  {
    id: 'music',
    title: 'Overture Music',
    description: 'Overture’s game-based music curriculum.',
    published: true,
    kind: 'curriculum',
    contentSource: 'code',
    levels: musicLevels as unknown as LevelLite[],
  },
  {
    id: 'musicwinter2026',
    title: 'Music Winter 2026',
    description: 'Overture’s Winter 2026 music curriculum.',
    published: true,
    kind: 'curriculum',
    contentSource: 'code',
    levels: musicWinter2026Levels as unknown as LevelLite[],
  },
];

export const scaffoldedCurriculumById = (id: string) =>
  scaffoldedCurricula.find((curriculum) => curriculum.id === id);
